import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LEARN } from "../../theme";
import WritingHandwritingPad from "../WritingHandwritingPad";
import { WRITING_L8 } from "../../../../data/writingCurriculum";
import {
  ChoiceRow,
  CollectedStrip,
  GameFrame,
  IconButton,
  RepeatDots,
  RoundBar,
  RoundCounter,
  ScorePill,
  SentenceFrame,
  SpeakerIcon,
  TargetChip,
  TimerPill,
  WordPicture,
  useCompleteOnce,
  useCountdown,
  useSpokenItem,
} from "./shared";

const NUMERALS = WRITING_L8.numberNames.map((n) => String(n.numeral));

/* ── Guided Numbers: trace twice, write three times ─────────────────────── */
export const ArrowNumbersGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const finish = useCompleteOnce(onComplete);
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState("trace");
  const [hits, setHits] = useState(0);
  const numeral = NUMERALS[index];
  const need = phase === "trace" ? 2 : 3;
  const word = WRITING_L8.numberNames[index]?.word;

  const accepted = () => {
    const nextHits = hits + 1;
    if (nextHits < need) {
      setHits(nextHits);
      speak?.(phase === "trace" ? `Trace ${word} again.` : `Write ${word} again.`, { interrupt: true });
      return;
    }
    if (phase === "trace") {
      setPhase("memory");
      setHits(0);
      speak?.("Both traces are saved. Now write it from memory.", { interrupt: true });
      return;
    }
    if (index + 1 >= NUMERALS.length) finish();
    else {
      speak?.(`Next is ${WRITING_L8.numberNames[index + 1].word}.`, { interrupt: true });
      setIndex((n) => n + 1);
      setPhase("trace");
      setHits(0);
    }
  };

  return (
    <GameFrame>
      <RoundBar
        left={
          <TargetChip glyph={numeral} caption={phase === "trace" ? `Trace ${word}` : `Write ${word}`} accent={activity.accent}>
            <RepeatDots total={need} done={hits} accent={activity.accent} />
          </TargetChip>
        }
        right={<RoundCounter done={index} total={NUMERALS.length} accent={activity.accent} />}
      />
      <WritingHandwritingPad
        key={`${numeral}-${phase}-${hits}`}
        target={numeral}
        accent={activity.accent}
        showsGuide={phase === "trace"}
        sfx={sfx}
        reduceMotion={reduceMotion}
        onAccepted={accepted}
        onRejected={() => onWrong?.()}
      />
    </GameFrame>
  );
};

/* ── Count and Write: tap each one to count, then write how many ────────── */
export const CountAndWriteGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const finish = useCompleteOnce(onComplete);
  const [index, setIndex] = useState(0);
  const [counted, setCounted] = useState([]);
  const round = WRITING_L8.countRounds[index];
  const line = `Count the ${round.pluralName}. Write ${round.count}.`;

  useSpokenItem(speak, line);
  useEffect(() => {
    setCounted([]);
  }, [round]);

  const tapItem = (i) => {
    if (counted.includes(i)) return;
    const next = [...counted, i];
    setCounted(next);
    sfx?.("pop", { volume: 0.5, rate: 1 + next.length * 0.04 });
    speak?.(String(next.length), { interrupt: true });
  };

  const tile = round.count > 6 ? "calc(var(--tile, 72px) * 0.78)" : "calc(var(--tile, 72px) * 0.95)";

  return (
    <GameFrame>
      <RoundBar
        left={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <IconButton label="Hear it again" onClick={() => speak?.(line, { interrupt: true })} accent={activity.accent} filled>
              <SpeakerIcon size={20} />
            </IconButton>
            <span style={{ fontWeight: 700, fontSize: 17, color: LEARN.color.muted }}>
              {counted.length ? `${counted.length} counted` : `Tap each ${round.singularName} to count`}
            </span>
          </div>
        }
        right={<RoundCounter done={index} total={WRITING_L8.countRounds.length} accent={activity.accent} />}
      />
      <ChoiceRow gap={8} style={{ padding: "2px 0" }}>
        {Array.from({ length: round.count }).map((_, i) => (
          <motion.div
            key={`${round.count}-${i}`}
            initial={reduceMotion ? false : { opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 380, damping: 20, delay: reduceMotion ? 0 : i * 0.05 }}
            style={{ position: "relative" }}
          >
            <WordPicture
              name={round.assetName}
              alt={`${round.singularName} ${i + 1}`}
              ariaLabel={counted.includes(i) ? `${round.singularName}, counted` : `Count this ${round.singularName}`}
              size={tile}
              accent={activity.accent}
              correct={counted.includes(i)}
              onClick={() => tapItem(i)}
              reduceMotion={reduceMotion}
            />
            {counted.includes(i) && (
              <span
                aria-hidden="true"
                style={{
                  position: "absolute", top: -6, right: -6, minWidth: 24, height: 24, padding: "0 6px", borderRadius: 999,
                  background: activity.accent, color: "#fff", fontWeight: 700, fontSize: 14, display: "grid", placeItems: "center",
                  boxShadow: LEARN.shadow.card,
                }}
              >
                {counted.indexOf(i) + 1}
              </span>
            )}
          </motion.div>
        ))}
      </ChoiceRow>
      <WritingHandwritingPad
        key={round.count}
        target={String(round.count)}
        accent={activity.accent}
        showsGuide={false}
        sfx={sfx}
        reduceMotion={reduceMotion}
        onAccepted={() => {
          speak?.(`${round.count} ${round.pluralName}.`, { interrupt: true });
          if (index + 1 >= WRITING_L8.countRounds.length) finish();
          else setIndex((n) => n + 1);
        }}
        onRejected={() => {
          onWrong?.();
          speak?.(`Count again. ${line}`, { interrupt: true });
        }}
      />
    </GameFrame>
  );
};

/* ── Fast Numbers: 0 to 9 in order for a friendly minute ────────────────── */
export const FastNumbersGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const finish = useCompleteOnce(onComplete);
  const [written, setWritten] = useState([]);
  const seconds = useCountdown(activity.seconds || 60, { onEnd: finish });
  const target = NUMERALS[written.length % 10];
  return (
    <GameFrame>
      <RoundBar
        left={
          <>
            <TimerPill seconds={seconds} accent={activity.accent} />
            <TargetChip glyph={target} caption={`Write ${WRITING_L8.numberNames[written.length % 10].word}`} accent={activity.accent} size="calc(var(--tile, 72px) * 0.8)" />
          </>
        }
        right={<ScorePill score={written.length} unit={written.length === 1 ? "number" : "numbers"} accent={activity.accent} />}
      />
      <CollectedStrip items={written} accent={activity.accent} reduceMotion={reduceMotion} empty="Start with zero" />
      <WritingHandwritingPad
        key={written.length}
        target={target}
        accent={activity.accent}
        showsGuide={false}
        sfx={sfx}
        reduceMotion={reduceMotion}
        acceptDelay={reduceMotion ? 100 : 480}
        onAccepted={() => {
          speak?.(WRITING_L8.numberNames[written.length % 10].word, { interrupt: true });
          setWritten((w) => [...w, target]);
        }}
        onRejected={() => onWrong?.()}
      />
    </GameFrame>
  );
};

/* ── Number in a Sentence ───────────────────────────────────────────────── */
export const NumberInSentenceGame = ({ activity, profile, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const age = Math.min(8, Math.max(1, Number(profile?.age) || 5));
  const finish = useCompleteOnce(onComplete);
  const [step, setStep] = useState(0);
  const [filled, setFilled] = useState("");
  const steps = [
    { target: String(age), before: "I am ", after: ".", line: `I am ${age}. Write ${age}.` },
    { target: "2", before: "I have ", after: " cats.", line: "Count the cats. I have 2 cats. Write 2." },
  ];
  const current = steps[step];

  useSpokenItem(speak, current.line);

  return (
    <GameFrame>
      <RoundBar
        left={
          <SentenceFrame before={current.before} after={current.after} filled={filled} accent={activity.accent} blankWidth="1.4em">
            {step === 1 && [0, 1].map((i) => (
              <WordPicture key={i} name="c_word_cat" alt="cat" size="calc(var(--tile, 72px) * 0.85)" accent={activity.accent} />
            ))}
          </SentenceFrame>
        }
        right={
          <>
            <IconButton label="Hear the sentence" onClick={() => speak?.(current.line, { interrupt: true })} accent={activity.accent} filled>
              <SpeakerIcon size={20} />
            </IconButton>
            <RoundCounter done={step} total={steps.length} accent={activity.accent} />
          </>
        }
      />
      <WritingHandwritingPad
        key={step}
        target={current.target}
        accent={activity.accent}
        showsGuide={false}
        sfx={sfx}
        reduceMotion={reduceMotion}
        onAccepted={() => {
          setFilled(current.target);
          speak?.(`${current.before}${current.target}${current.after}`, { interrupt: true });
          window.setTimeout(() => {
            setFilled("");
            if (step + 1 >= steps.length) finish();
            else setStep(step + 1);
          }, reduceMotion ? 200 : 900);
        }}
        onRejected={() => onWrong?.()}
      />
    </GameFrame>
  );
};

/* ── Number Words: numeral, then its word ───────────────────────────────── */
export const NumberWordsGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const pairs = WRITING_L8.numberNames.slice(1, 6);
  const finish = useCompleteOnce(onComplete);
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState("numeral");
  const [done, setDone] = useState([]);
  const item = pairs[index];
  const target = phase === "numeral" ? String(item.numeral) : item.word;

  useSpokenItem(speak, phase === "numeral" ? `Write the numeral ${item.word}.` : `Now write the word ${item.word}.`);

  const accepted = () => {
    if (phase === "numeral") {
      setPhase("word");
      return;
    }
    setDone((d) => [...d, `${item.numeral} ${item.word}`]);
    if (index + 1 >= pairs.length) finish();
    else {
      setIndex((n) => n + 1);
      setPhase("numeral");
    }
  };

  return (
    <GameFrame>
      <RoundBar
        left={<TargetChip glyph={target} caption={phase === "numeral" ? "Write the numeral" : "Write the word"} accent={activity.accent} />}
        right={<RoundCounter done={index} total={pairs.length} accent={activity.accent} />}
      />
      <CollectedStrip items={done} accent={activity.accent} reduceMotion={reduceMotion} empty="Both partners appear here" />
      <WritingHandwritingPad
        key={`${item.numeral}-${phase}`}
        target={target}
        accent={activity.accent}
        showsGuide={false}
        mode={phase === "word" ? "word" : "letter"}
        hintTitle="Show me"
        sfx={sfx}
        reduceMotion={reduceMotion}
        onAccepted={accepted}
        onRejected={() => onWrong?.()}
      />
    </GameFrame>
  );
};
